import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';
import api from '../../services/api';

const fmt = (n) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(n || 0);
const fmtK = (n) => { const v = n || 0; return v >= 1000000 ? `$${(v/1000000).toFixed(1)}M` : v >= 1000 ? `$${(v/1000).toFixed(0)}K` : `$${v}`; };

const COLORS = ['#6366f1','#22d3ee','#34d399','#fbbf24','#f87171','#a78bfa'];

const StatCard = ({ label, value, sub, color = '#6366f1', icon }) => (
  <div style={{ background: 'var(--bg-card)', borderRadius: 12, padding: '20px 24px', border: '1px solid var(--border)', minWidth: 0 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
      <span style={{ fontSize: 12, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: 1 }}>{label}</span>
      <span style={{ fontSize: 20 }}>{icon}</span>
    </div>
    <div style={{ fontSize: 28, fontWeight: 700, color, marginBottom: 4 }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{sub}</div>}
  </div>
);

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/admin/reportes/dashboard')
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ color: 'var(--text-3)', padding: 40, textAlign: 'center' }}>Cargando...</div>;
  if (!data) return <div style={{ color: '#ef4444', padding: 40 }}>Error cargando datos</div>;

  const mesesLabel = (data.revenueMensual || []).map(m => {
    const [, mes] = m.mes.split('-');
    const nombres = ['','Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
    return { ...m, label: nombres[parseInt(mes)] || m.mes };
  });

  return (
    <div style={{ padding: 28 }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--text-1)' }}>Panel SaaS</h1>
        <p style={{ margin: '4px 0 0', color: 'var(--text-3)', fontSize: 14 }}>Resumen de tu negocio Prestam.io</p>
      </div>

      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, marginBottom: 28 }}>
        <StatCard label="MRR" value={fmtK(data.mrr)} sub="Ingreso mensual recurrente" color="#6366f1" icon="💰" />
        <StatCard label="ARR" value={fmtK(data.arr)} sub="Ingreso anual proyectado" color="#22d3ee" icon="📅" />
        <StatCard label="Clientes Activos" value={data.clientesActivos} sub={`${data.clientesTotal} total | ${data.clientesTrial} en trial`} color="#34d399" icon="🏢" />
        <StatCard label="Sillas Activas" value={data.sillasActivas} sub="Licencias en uso" color="#fbbf24" icon="💺" />
        <StatCard label="Pipeline" value={fmtK(data.pipeline?.valor_ponderado)} sub={`${data.pipeline?.oportunidades ?? 0} oportunidades abiertas`} color="#a78bfa" icon="🎯" />
        <StatCard label="Cobros Pendientes" value={fmtK(data.pagosPendientes?.monto_total)} sub={`${data.pagosPendientes?.total ?? 0} pagos por cobrar`} color="#f87171" icon="⏳" />
      </div>

      {/* Charts Row 1 */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20, marginBottom: 20 }}>
        {/* Revenue chart */}
        <div style={{ background: 'var(--bg-card)', borderRadius: 12, padding: 20, border: '1px solid var(--border)' }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 14, color: 'var(--text-2)', fontWeight: 600 }}>Ingresos Mensuales (últimos 12 meses)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={mesesLabel}>
              <defs>
                <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <XAxis dataKey="label" tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={fmtK} tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8 }}
                labelStyle={{ color: 'var(--text-2)' }}
                formatter={(v) => [fmt(v), 'Ingresos']}
              />
              <Area type="monotone" dataKey="ingresos" stroke="#6366f1" fill="url(#colorRev)" strokeWidth={2} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* MRR by plan */}
        <div style={{ background: 'var(--bg-card)', borderRadius: 12, padding: 20, border: '1px solid var(--border)' }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 14, color: 'var(--text-2)', fontWeight: 600 }}>MRR por Plan</h3>
          {data.mrrPorPlan?.length ? (
            <>
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie data={data.mrrPorPlan} dataKey="mrr" nameKey="nombre" cx="50%" cy="50%" outerRadius={70} innerRadius={40}>
                    {data.mrrPorPlan.map((entry, i) => (
                      <Cell key={i} fill={entry.color || COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => fmt(v)} contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8 }} />
                </PieChart>
              </ResponsiveContainer>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                {data.mrrPorPlan.map((p, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: p.color || COLORS[i] }}/>
                      <span style={{ color: 'var(--text-2)' }}>{p.nombre}</span>
                      <span style={{ color: 'var(--text-4)', fontSize: 11 }}>({p.clientes})</span>
                    </div>
                    <span style={{ color: 'var(--text-1s)', fontWeight: 600 }}>{fmtK(p.mrr)}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div style={{ color: 'var(--text-4)', textAlign: 'center', paddingTop: 40 }}>Sin datos</div>
          )}
        </div>
      </div>

      {/* Charts Row 2 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        {/* New clients */}
        <div style={{ background: 'var(--bg-card)', borderRadius: 12, padding: 20, border: '1px solid var(--border)' }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 14, color: 'var(--text-2)', fontWeight: 600 }}>Nuevos Clientes (últimos 6 meses)</h3>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={(data.nuevosClientes || []).map(m => {
              const [, mes] = m.mes.split('-');
              const nombres = ['','Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
              return { ...m, label: nombres[parseInt(mes)] || m.mes };
            })}>
              <XAxis dataKey="label" tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8 }} labelStyle={{ color: 'var(--text-2)' }} />
              <Bar dataKey="nuevos" fill="#22d3ee" radius={[4,4,0,0]} name="Nuevos" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Client status */}
        <div style={{ background: 'var(--bg-card)', borderRadius: 12, padding: 20, border: '1px solid var(--border)' }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 14, color: 'var(--text-2)', fontWeight: 600 }}>Estado de Clientes</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {[
              { label: 'Activos',     value: data.clientesActivos,  color: '#34d399' },
              { label: 'Trial',       value: data.clientesTrial,    color: '#fbbf24' },
              { label: 'Prospectos',  value: data.clientesProspecto, color: '#6366f1' },
              { label: 'Churn 3m',    value: data.churn,            color: '#f87171' },
            ].map((s, i) => (
              <div key={i} style={{ background: 'var(--bg-app)', borderRadius: 8, padding: '14px 16px', border: `1px solid ${s.color}30` }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 16 }}>
            <button
              onClick={() => navigate('/superadmin/clientes')}
              style={{ width: '100%', padding: '8px 0', borderRadius: 8, border: 'none', background: '#1e3a5f', color: '#60a5fa', cursor: 'pointer', fontSize: 13 }}
            >
              Ver todos los clientes →
            </button>
          </div>
        </div>
      </div>

      {/* Por vencer */}
      <PorVencer />
    </div>
  );
}

function PorVencer() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get('/admin/reportes/por-vencer').then(r => setItems(r.data)).catch(() => {});
  }, []);

  if (!items.length) return null;

  return (
    <div style={{ background: 'var(--bg-card)', borderRadius: 12, padding: 20, border: '1px solid #f59e0b40' }}>
      <h3 style={{ margin: '0 0 14px', fontSize: 14, color: '#fbbf24', fontWeight: 600 }}>
        ⚠ Suscripciones por vencer (próximos 30 días)
      </h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.map(s => (
          <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: 'var(--bg-app)', borderRadius: 8, border: '1px solid var(--border)' }}>
            <div>
              <span style={{ fontWeight: 600, color: 'var(--text-1s)', fontSize: 14 }}>{s.empresa}</span>
              <span style={{ color: 'var(--text-3)', fontSize: 12, marginLeft: 10 }}>{s.plan_nombre} · {s.sillas} sillas</span>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ color: s.dias_restantes <= 7 ? '#f87171' : '#fbbf24', fontWeight: 600, fontSize: 13 }}>
                {s.dias_restantes} días
              </div>
              <div style={{ color: 'var(--text-3)', fontSize: 11 }}>{s.fecha_fin}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
